import React, { useState, useMemo, useCallback } from "react";
import PropTypes from 'prop-types';
import { TextField } from '@mui/material';

const Table = ({ columns, data, hiddenColumns, updateLLViewSettings }) => {
  const [searchValue, setSearchValue] = useState('');

  // Memoize search handler to prevent unnecessary re-renders
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchValue(value);
    updateLLViewSettings('from_activity_search', value || null);
  }, [updateLLViewSettings]);

  // Filter out hidden columns
  const visibleColumns = useMemo(() => {
    if (!hiddenColumns || hiddenColumns.length === 0) return columns;
    return columns.filter(col => !hiddenColumns.includes(col.accessor));
  }, [columns, hiddenColumns]);
  
  // Check for required props
  if (!columns || !Array.isArray(columns)) {
    console.warn('Table: columns prop is required and must be an array');
    return null;
  }

  if (!data || !Array.isArray(data)) {
    console.warn('Table: data prop is required and must be an array');
    return null;
  }

  return (
    <div className="table">
      <table>
        <thead>
          <tr className="tr">
            {visibleColumns.map((column) => (
              <th key={column.accessor || column.Header} className="th">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {column.Header}
                  &nbsp;&nbsp;&nbsp;
                  <TextField
                    placeholder="Filter"
                    variant="outlined"
                    style={{ backgroundColor: 'white' }}
                    size="small"
                    value={searchValue}
                    InputLabelProps={{
                      style: {
                        height: 24,
                        top: '-6px',
                      },
                    }}
                    inputProps={{
                      style: {
                        height: 24,
                        padding: '0 0px',
                      },
                    }}
                    onChange={handleSearchChange}
                    aria-label={`Filter ${column.Header}`}
                  />
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr key={row.id || rowIndex} className="tr">
              {visibleColumns.map((column) => {
                const cellValue = row[column.accessor];
                return (
                  <td key={`${row.id || rowIndex}-${column.accessor}`} className="td">
                    {column.Cell ?
                      column.Cell({
                        value: cellValue,
                        row: { values: row, original: row },
                        column
                      }) :
                      cellValue
                    }
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// PropTypes for better development experience and error catching
Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.string.isRequired,
      accessor: PropTypes.string.isRequired,
      Cell: PropTypes.func,
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  hiddenColumns: PropTypes.arrayOf(PropTypes.string),
  updateLLViewSettings: PropTypes.func.isRequired,
};

// Default props for optional properties
Table.defaultProps = {
  columns: [],
  data: [],
};

export default Table;